/**
 * The facts card on a task page: what state the task is in, when it is due, and
 * the seven fields the product could never set.
 *
 * `startDate`, `estimateHours` and `billable` have been on the task since the
 * schema was written and the API has always accepted them, but no screen wrote
 * or showed them - so every task read as billable with no estimate, and the only
 * honest answer to "is this chargeable" was "nobody has ever said". They are
 * editable here rather than in a dialog because they are the record itself, and
 * a dialog to tick one box is a dialog nobody opens.
 *
 * `assignees` and `milestoneId` are the same story with a longer silence. Both
 * columns are as old as the table, both have always been accepted by the routes,
 * and neither had a control anywhere in the CRM: the register that arrived from
 * the old system carries no owner on any task at all, and the phase ids on
 * two thirds of them had never been drawn on a screen. They belong on this card
 * for the reason the other three do - they are the record, and the moment you
 * want to change them is the moment you are looking at it.
 *
 * `visibleToClient` is the sixth, and it is not like the others: it is the only
 * field on this card that decides what somebody OUTSIDE the company reads. The
 * client portal lists a project's tasks and filters on exactly this flag, so the
 * tick box below is the whole of the product's answer to "can the customer see
 * this". It is labelled with what it does to the client rather than with the
 * column's name, because an operator deciding to hide a task is thinking about
 * who reads it, not about a boolean - and it sits under a heading of its own so
 * it cannot be skimmed as a second billing question.
 *
 * `tags` is the seventh and the newest column of the set. It has a twin on the
 * project (same type, same ceilings, same normaliser, same GIN index) and the
 * register filters on it by exact array containment, which is the reason it is
 * written by the shared chip control rather than by a field of this card's own:
 * two writers that disagree about trimming or case make two vocabularies that
 * look identical and never match each other.
 *
 * The read-only lines above them stay read-only on purpose: status has its own
 * control in the page head, and priority and the due date belong to the task
 * dialog. This card writes exactly the fields that had nowhere else to go.
 *
 * `Row` and `BODY` are local copies of the shapes `ProjectOverview` and
 * `TicketDetail` also declare for themselves. The repeat is deliberate and
 * already the house pattern: a hairline row is nine lines of markup, and a
 * shared one would have to grow a prop for every card that wants a slightly
 * different line - which is how a "shared" component becomes the widest type in
 * the app.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Loader2, Save } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { updateTask, type Milestone, type Task } from "../../../lib/projects";
import { Btn, Card, CardHead, CheckField, MenuField, StatusPill, fmtDate, useToast } from "../../ui";
import { TagChips } from "../_shared/formBits";
import { UserPicker } from "../_shared/UserPicker";
import { isOverdue, toIso } from "../projects/lib";
import { TAGS_MAX, TAG_MAX_LEN } from "../projects/projectFields";
import { taskTone } from "../projects/status";
import { priorityTone } from "./priority";
import { taskErrorKey } from "./taskErrors";

/**
 * A card body's padding.
 *
 * `crm-card` is a surface and nothing else - the radius and the shadow, no
 * padding - so anything that is not a `crm-card-head` or a `crm-rows` has to say
 * where its edges are.
 */
const BODY = "px-6 pb-5 pt-3";

/** One label-and-figure line in the facts card. The same row `ProjectOverview`
 *  uses, so a task and a project read the same way. */
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-t border-[var(--crm-hair)] py-2.5 text-[13px] first:border-t-0 first:pt-0">
      <span className="crm-mut">{label}</span>
      <span className="crm-push crm-tnum font-semibold">{value}</span>
    </div>
  );
}

/** An ISO instant as the `YYYY-MM-DD` a `DatePicker` takes. */
function dateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

/** Hours as a form value. An estimate nobody set is an empty field, not a zero:
 *  "0 h" is a claim that the task takes no work. */
function hoursInput(hours: number | null): string {
  return hours == null ? "" : String(hours);
}

/**
 * A list of ids as one comparable value.
 *
 * The sync effect below has to fire when the PEOPLE change and not when a
 * refetch hands back the same names in a new array. Depending on the array
 * itself would reset the picker under the operator's hand every time the page
 * reloaded the task for an unrelated reason - a status change does exactly that.
 * Ids are cuids, so a comma can never appear inside one.
 */
function idKey(ids: readonly string[]): string {
  return ids.join(",");
}

/**
 * The tags as one comparable value, for the same sync effect and the same dirty
 * check.
 *
 * A newline joins them rather than the comma `idKey` uses, because a tag is free
 * text: the chip control commits on a comma so nothing typed here can contain
 * one, but a label that arrived from an import never went through that control.
 * A single-line field cannot produce a newline, so this key cannot collide.
 *
 * Being wrong here can only ever light the Save button up on a card nobody
 * changed - it writes nothing by itself - but the rows it would light up are the
 * imported ones, which are precisely the ones nobody should be invited to
 * rewrite by accident.
 */
function tagKey(tags: readonly string[]): string {
  return tags.join("\n");
}

export function TaskFacts({
  task,
  milestones,
  onSaved,
  className,
}: {
  task: Task;
  /**
   * The phases of the project this task sits on: `null` while they are unknown
   * (still loading, unreadable, or no project at all), an array once the answer
   * is in. Handed down rather than fetched here - the page above already reads
   * the project to name the phase in its head, and a second request for the same
   * list is one the operator waits for twice.
   */
  milestones?: Milestone[] | null;
  onSaved: (t: Task) => void;
  /** Passed through to the card. The task page uses it to place this card in
   *  the single-column order it wants on a phone (`crm-w2`). */
  className?: string;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const [start, setStart] = useState(() => dateInput(task.startDate));
  const [estimate, setEstimate] = useState(() => hoursInput(task.estimateHours));
  const [assignees, setAssignees] = useState<string[]>(() => task.assignees);
  const [tags, setTags] = useState<string[]>(() => task.tags);
  const [milestoneId, setMilestoneId] = useState(() => task.milestoneId ?? "");
  const [billable, setBillable] = useState(task.billable);
  const [visibleToClient, setVisibleToClient] = useState(task.visibleToClient);
  const [saving, setSaving] = useState(false);

  const savedAssignees = idKey(task.assignees);
  const savedTags = tagKey(task.tags);

  // The fields follow the record. Reading `task` only at mount is how the
  // project's progress slider ended up showing one number while the card next to
  // it showed another: the page reloads the task after a status change, and a
  // form that ignored that would write yesterday's values back on the next save.
  useEffect(() => {
    setStart(dateInput(task.startDate));
    setEstimate(hoursInput(task.estimateHours));
    setAssignees(savedAssignees ? savedAssignees.split(",") : []);
    setTags(savedTags ? savedTags.split("\n") : []);
    setMilestoneId(task.milestoneId ?? "");
    setBillable(task.billable);
    setVisibleToClient(task.visibleToClient);
  }, [
    task.startDate,
    task.estimateHours,
    savedAssignees,
    savedTags,
    task.milestoneId,
    task.billable,
    task.visibleToClient,
  ]);

  const late = isOverdue(task);

  /**
   * Whether the phase field is drawn at all.
   *
   * Not while the list is unknown, and not on a project that has no phases: a
   * select whose single option is "no milestone" is a control that cannot do
   * anything. A task that ALREADY carries a phase keeps the field even where the
   * project came back with none, because hiding it would hide the fact - and
   * because `save` below is allowed to send `milestoneId` only when this is
   * true, so a hidden field can never quietly unfile a task.
   */
  const canFilePhase = milestones != null && (milestones.length > 0 || task.milestoneId != null);
  /**
   * A task whose own phase is missing from the project's list. Unreachable in
   * practice - the column is a foreign key and the routes refuse a milestone
   * belonging to another project - but a dropdown whose value matches no option
   * silently shows the FIRST one instead, so without this row the card would
   * read "no milestone" for a task that has one, and the save button would then
   * offer to make that true. The option is labelled with the field's own name
   * because a phase we cannot find is a phase we cannot name.
   */
  const orphanPhase = canFilePhase && task.milestoneId != null && !milestones?.some((m) => m.id === task.milestoneId);

  const dirty =
    start !== dateInput(task.startDate) ||
    estimate.trim() !== hoursInput(task.estimateHours) ||
    idKey(assignees) !== savedAssignees ||
    tagKey(tags) !== savedTags ||
    (canFilePhase && milestoneId !== (task.milestoneId ?? "")) ||
    billable !== task.billable ||
    visibleToClient !== task.visibleToClient;

  async function save() {
    const hours = Number(estimate.trim());
    setSaving(true);
    try {
      const next = await updateTask(task.id, {
        // An empty field clears the column rather than leaving the old value in
        // place, which is the only way to take back an estimate.
        startDate: toIso(start) ?? null,
        estimateHours: estimate.trim() === "" || !Number.isFinite(hours) ? null : hours,
        assignees,
        // Always sent, empty list included. The route reads `[]` as "clear the
        // tags" and an ABSENT key as "leave them alone" - two different
        // instructions - so taking the last chip off a task has to travel as an
        // empty array or the removal would be silently dropped.
        tags,
        // Only where the field was on screen. A task whose phases never arrived
        // would otherwise be saved with the null an unrendered select holds,
        // unfiling it from a milestone nobody touched.
        ...(canFilePhase ? { milestoneId: milestoneId || null } : {}),
        billable,
        visibleToClient,
      });
      onSaved(next);
    } catch (err) {
      // The phase is the one field here the server can refuse outright - it
      // checks that the milestone exists and belongs to this project - so the
      // message says which of the two happened rather than "something broke".
      toast(t(taskErrorKey(err)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className={className}>
      <CardHead title={t("projects.details")} />
      <div className={BODY}>
        <Row
          label={t("projects.statusCol")}
          value={<StatusPill tone={taskTone(task.status)}>{t(`tasks.status.${task.status}`)}</StatusPill>}
        />
        <Row
          label={t("tasks.priority")}
          value={<StatusPill tone={priorityTone(task.priority)}>{t(`tasks.prio.${task.priority}`)}</StatusPill>}
        />
        <Row
          label={t("tasks.dueDate")}
          value={
            <span className={late ? "text-red-600" : undefined}>
              {fmtDate(task.dueDate)}
              {late ? ` (${t("projects.overdue")})` : ""}
            </span>
          }
        />
        <Row label={t("tasks.created")} value={fmtDate(task.createdAt)} />
        {task.completedAt ? <Row label={t("tasks.completed")} value={fmtDate(task.completedAt)} /> : null}

        {/* The editable half, under a hairline of its own so it is visibly a
            form and not three more figures somebody else decided. */}
        <div className="mt-3.5 border-t border-[var(--crm-hair)] pt-3.5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <span className="crm-lab">{t("tasks.startDate")}</span>
              <DatePicker
                value={start}
                onChange={setStart}
                locale={locale}
                placeholder={t("tasks.startDate")}
              />
            </div>
            <div>
              <span className="crm-lab">{t("tasks.estimateHours")}</span>
              <label className="crm-inp">
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  inputMode="decimal"
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                />
              </label>
            </div>
          </div>

          {/* Always drawn, and the empty state is stated in words. On the day
              this shipped not one task in the register had an owner, so an
              assignee field that showed nothing when nobody was picked would
              read as a control that failed to load rather than as the truth:
              this task is on nobody. */}
          <div className="mt-2.5">
            <span className="crm-lab">{t("projects.assignees")}</span>
            <UserPicker multiple value={assignees} onChange={setAssignees} />
            {assignees.length === 0 ? (
              <p className="crm-mut mt-1.5 text-[12.5px]">{t("projects.unassigned")}</p>
            ) : null}
          </div>

          {/* The task's own labels, written by the SAME control the project form
              uses and at the same two ceilings. That is not tidiness: tags are
              matched by exact array containment, so the writer decides what the
              filter can ever find, and a second control here that trimmed or
              folded case differently would build a vocabulary that looks
              identical on screen and matches nothing. `crm-projects/lib/tags.ts`
              states the rule for both columns and says what changing it would
              cost.

              Here rather than in a card of its own: this is the form that owns
              the task's columns, and a second writing surface for one column is
              the arrangement where two screens end up disagreeing about it. */}
          <div className="mt-2.5">
            <span className="crm-lab">{t("tasks.tags")}</span>
            <TagChips
              tags={tags}
              onChange={setTags}
              placeholder={t("tasks.tagsPlaceholder")}
              maxLen={TAG_MAX_LEN}
              maxCount={TAGS_MAX}
              removeLabel={t("common.delete")}
            />
          </div>

          {canFilePhase ? (
            <div className="mt-2.5">
              <span className="crm-lab">{t("tasks.milestone")}</span>
              {/* The app's own menu, like the same field in the create dialog:
                  a native select inside this pill draws no arrow and then opens
                  a grey OS list over the card. "No milestone" is a listed option
                  rather than an empty first row - taking a task out of a phase
                  is a decision, and a blank line does not look like one. */}
              <MenuField
                variant="form"
                value={milestoneId}
                onChange={setMilestoneId}
                label={t("tasks.milestone")}
                options={[
                  { value: "", label: t("projects.noMilestone") },
                  ...(orphanPhase && task.milestoneId
                    ? [{ value: task.milestoneId, label: t("projects.milestone") }]
                    : []),
                  ...(milestones ?? []).map((m) => ({ value: m.id, label: m.name })),
                ]}
              />
            </div>
          ) : null}

          <CheckField
            className="mt-2.5"
            checked={billable}
            onChange={setBillable}
            label={t("tasks.billable")}
          />

          {/* Its own heading rather than a second bare tick box under the
              first. The two boxes answer different people: "billable" is a
              question about our invoice, this one is a question about what the
              customer reads, and two identical unlabelled fields in a stack
              invite the reader to take the second for more billing. The heading
              names the subject, the box states the setting. */}
          <span className="crm-lab">{t("projects.clientVisibility")}</span>
          <CheckField
            checked={visibleToClient}
            onChange={setVisibleToClient}
            label={t("projects.visibleToClient")}
          />

          <div className="mt-3 flex justify-end">
            <Btn tone="dark" onClick={() => void save()} disabled={saving || !dirty}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {t("common.save")}
            </Btn>
          </div>
        </div>
      </div>
    </Card>
  );
}
